import { useRoute } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { ClientService } from "@/services/ClientService";
import { PageHeader } from "@/components/ui/PageHeader";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, ArrowLeft, Building2, Store } from "lucide-react";
import { useLocation } from "wouter";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { ContactsTab } from "./contacts-tab";
import { apiJson } from "@/lib/api";

export default function ClientDetailPage() {
    const [, params] = useRoute("/admin/clients/:id");
    const [, setLocation] = useLocation();
    const id = params?.id ? parseInt(params.id) : 0;

    const { data, isLoading } = useQuery({
        queryKey: ["client", id],
        queryFn: () => ClientService.getClient(id),
        enabled: !!id,
    });

    // Phase 4.7A: Fetch bookings for this client
    const { data: bookingsData, isLoading: bookingsLoading } = useQuery<any[]>({
        queryKey: ["bookings", "client", id],
        queryFn: () => apiJson(`/api/bookings?clientId=${id}`),
        enabled: !!id,
    });

    const client = data?.data?.client;

    if (isLoading) {
        return (
            <div className="flex h-[50vh] items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
        );
    }

    if (!client) {
        return (
            <div className="p-6">
                <div className="text-center">
                    <h2 className="text-xl font-semibold">Client not found</h2>
                    <Button variant="link" onClick={() => setLocation("/admin/clients")}>
                        Back to Client List
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6 space-y-6">
            <Button
                variant="ghost"
                className="mb-2 pl-0 hover:pl-2 transition-all"
                onClick={() => setLocation("/admin/clients")}
            >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Clients
            </Button>

            <PageHeader
                title={client.name}
                description={
                    <div className="flex items-center gap-2 mt-1">
                        <Badge variant={client.isActive ? "default" : "secondary"}>
                            {client.isActive ? "Active" : "Inactive"}
                        </Badge>
                        {client.description && <span className="text-muted-foreground">• {client.description}</span>}
                    </div>
                }
            />

            <Tabs defaultValue="overview" className="space-y-4">
                <TabsList>
                    <TabsTrigger value="overview">Overview</TabsTrigger>
                    <TabsTrigger value="advertisers">
                        Linked Advertisers
                        <Badge variant="secondary" className="ml-2 h-5 min-w-[20px] px-1">
                            {client.advertisers?.length || 0}
                        </Badge>
                    </TabsTrigger>
                    <TabsTrigger value="contacts">Contacts</TabsTrigger>
                    {/* Phase 4.7A: Enabled Bookings tab */}
                    <TabsTrigger value="bookings">
                        Bookings
                        <Badge variant="secondary" className="ml-2 h-5 min-w-[20px] px-1">
                            {bookingsData?.length || 0}
                        </Badge>
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="overview" className="space-y-4">
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                        <Card>
                            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                <CardTitle className="text-sm font-medium">Billing Details</CardTitle>
                                <Building2 className="h-4 w-4 text-muted-foreground" />
                            </CardHeader>
                            <CardContent>
                                <div className="text-sm space-y-2 mt-2">
                                    <div>
                                        <span className="font-semibold block text-xs text-muted-foreground">Legal Name</span>
                                        {client.billingName || "Same as client name"}
                                    </div>
                                    <div>
                                        <span className="font-semibold block text-xs text-muted-foreground">VAT Number</span>
                                        {client.vatNumber || "Not recorded"}
                                    </div>
                                    <div>
                                        <span className="font-semibold block text-xs text-muted-foreground">Address</span>
                                        <div className="whitespace-pre-wrap">{client.billingAddress || "Not recorded"}</div>
                                    </div>
                                    <div>
                                        <span className="font-semibold block text-xs text-muted-foreground">Payment Terms</span>
                                        {client.paymentTerms || "Standard"}
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                <CardTitle className="text-sm font-medium">Primary Contact</CardTitle>
                                <Building2 className="h-4 w-4 text-muted-foreground" />
                            </CardHeader>
                            <CardContent>
                                <div className="text-sm space-y-2 mt-2">
                                    <div>
                                        <span className="font-semibold block text-xs text-muted-foreground">Name</span>
                                        {client.primaryContactName || "Not recorded"}
                                    </div>
                                    <div>
                                        <span className="font-semibold block text-xs text-muted-foreground">Email</span>
                                        {client.primaryContactEmail ? (
                                            <a href={`mailto:${client.primaryContactEmail}`} className="text-blue-600 hover:underline">
                                                {client.primaryContactEmail}
                                            </a>
                                        ) : "Not recorded"}
                                    </div>
                                    <div>
                                        <span className="font-semibold block text-xs text-muted-foreground">Phone</span>
                                        {client.primaryContactPhone || "Not recorded"}
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </TabsContent>

                <TabsContent value="advertisers">
                    <Card>
                        <CardHeader>
                            <CardTitle>Linked Advertisers</CardTitle>
                        </CardHeader>
                        <CardContent>
                            {client.advertisers && client.advertisers.length > 0 ? (
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Advertiser Name</TableHead>
                                            <TableHead>Status</TableHead>
                                            <TableHead>Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {client.advertisers.map((advertiser) => (
                                            <TableRow key={advertiser.id}>
                                                <TableCell className="font-medium">{advertiser.name}</TableCell>
                                                <TableCell>
                                                    <Badge variant={advertiser.isActive ? "outline" : "secondary"}>
                                                        {advertiser.isActive ? "Active" : "Inactive"}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell>
                                                    <Button variant="ghost" size="sm" onClick={() => setLocation(`/admin/advertisers?search=${encodeURIComponent(advertiser.name)}`)}>
                                                        View
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            ) : (
                                <div className="flex flex-col items-center justify-center p-8 text-center text-muted-foreground">
                                    <Store className="h-10 w-10 mb-4 opacity-20" />
                                    <p>No advertisers linked to this client yet.</p>
                                    <p className="text-sm mt-1">
                                        Go to <a className="underline cursor-pointer" onClick={() => setLocation('/admin/advertisers')}>Advertiser Management</a> to link advertisers.
                                    </p>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="contacts">
                    <ContactsTab clientId={id} />
                </TabsContent>

                {/* Phase 4.7A: Bookings Tab */}
                <TabsContent value="bookings">
                    <Card>
                        <CardHeader>
                            <CardTitle>Client Bookings</CardTitle>
                        </CardHeader>
                        <CardContent>
                            {bookingsLoading ? (
                                <div className="flex items-center justify-center p-8">
                                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                                </div>
                            ) : bookingsData && bookingsData.length > 0 ? (
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Booking Name</TableHead>
                                            <TableHead>Advertiser</TableHead>
                                            <TableHead>Cycle</TableHead>
                                            <TableHead>Status</TableHead>
                                            <TableHead className="text-right">Value</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {bookingsData.map((booking: any) => (
                                            <TableRow key={booking.id}>
                                                <TableCell className="font-medium">
                                                    {booking.name || `Booking #${booking.id}`}
                                                </TableCell>
                                                <TableCell>
                                                    {booking.advertiserName || "—"}
                                                </TableCell>
                                                <TableCell>
                                                    {booking.cycleName || (booking.cycleId ? `Cycle ${booking.cycleId}` : "—")}
                                                </TableCell>
                                                <TableCell>
                                                    <Badge variant={booking.status === "confirmed" ? "default" : "secondary"}>
                                                        {booking.status || "Draft"}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    {booking.bookingValue ? `€${Number(booking.bookingValue).toLocaleString()}` : "—"}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            ) : (
                                <div className="flex flex-col items-center justify-center p-8 text-center text-muted-foreground">
                                    <Store className="h-10 w-10 mb-4 opacity-20" />
                                    <p>No bookings for this client yet.</p>
                                    <p className="text-sm mt-1">
                                        Go to <a className="underline cursor-pointer" onClick={() => setLocation('/admin/bookings')}>Booking Management</a> to create bookings.
                                    </p>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>
        </div>
    );
}
